import { useCallback, useEffect, useState } from "react";
import {
  REPRODUCTIVE_STATUS_LABELS,
  REPRODUCTIVE_STATUS_VALUES,
  type AllergiesValue,
  type CorrectMedicalProfile,
  type MedicalProfileKind,
  type MedicalProfileSectionDto,
  type MedicalProfileValue,
  type MedicationItem,
  type MedicationsValue,
  type ReproductiveStatusValue,
} from "@care-by-ella/contracts";
import {
  correctMedicalProfile,
  fetchMedicalProfile,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-863 (Ö-211, groups 3/5/6) — the doctor's correction of medications,
// allergies and reproductive status, BESIDE what the patient declared.
//
// Emelie 2026-09-22, verbatim:
// "Patientens ursprungliga formulärsvar är oföränderliga original. Kirill korrigerar den strukturerade, aktuella medicinska profilen genom en rättad version bredvid originalet."
//
// Same three rules as the measurement section above it:
//   · the STATEMENT is the unit — the whole medication list is corrected at
//     once, pre-filled, so nothing he leaves alone is dropped;
//   · the original stays on screen, marked replaced;
//   · the reason is compulsory, and a correction that changes nothing is
//     refused before it is sent.
//
// Doctor only, like the route: anyone else gets 403 and the section does not
// render.

const KIND_TITLE: Record<MedicalProfileKind, string> = {
  medications: "Läkemedel",
  allergies: "Allergier och överkänslighet",
  reproductive_status: "Graviditet, amning och reproduktiv status",
};

const YES_NO_UNSURE: Record<string, string> = {
  ja: "Ja",
  nej: "Nej",
  osaker: "Osäker",
};

const ERROR_TEXT: Record<string, string> = {
  medical_profile_already_corrected:
    "Uppgiften har redan rättats. Ladda om sidan och rätta den aktuella versionen.",
  medical_profile_unchanged:
    "Rättelsen ändrar ingenting jämfört med nuvarande uppgift.",
  medical_profile_entry_not_found: "Uppgiften som skulle rättas hittades inte.",
  validation_failed: "Något fält är ogiltigt. Kontrollera och försök igen.",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function medicationLine(m: MedicationItem): string {
  const parts = [m.strength, m.frequency].filter(Boolean);
  const reason = m.reason ? ` — skäl: ${m.reason}` : "";
  return `${m.name}${parts.length ? ` (${parts.join(", ")})` : ""}${reason}`;
}

function describe(
  kind: MedicalProfileKind,
  value: MedicalProfileValue,
): string[] {
  if (kind === "medications") {
    const v = value as MedicationsValue;
    const lines = v.current.length
      ? v.current.map(medicationLine)
      : ["Inga aktuella läkemedel angivna."];
    if (v.weightMedicineHistory) {
      lines.push(
        `Tidigare/nuvarande vikt- eller diabetesläkemedel: ${YES_NO_UNSURE[v.weightMedicineHistory]}` +
          (v.weightMedicineHistoryDetail
            ? ` — ${v.weightMedicineHistoryDetail}`
            : ""),
      );
    }
    return lines;
  }
  if (kind === "allergies") {
    const v = value as AllergiesValue;
    if (!v.hasAllergies) return ["Ej angivet."];
    return [
      `Kända läkemedelsallergier: ${YES_NO_UNSURE[v.hasAllergies]}` +
        (v.detail ? ` — ${v.detail}` : ""),
    ];
  }
  const v = value as ReproductiveStatusValue;
  return [v.status ? REPRODUCTIVE_STATUS_LABELS[v.status] : "Ej angivet."];
}

export function PatientMedicalProfile({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  // TES-1040 — no "Rätta"/"Komplettera" in the admin's read-only view.
  const readOnly = useReadOnly();
  const [sections, setSections] = useState<MedicalProfileSectionDto[] | null>(
    null,
  );
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MedicalProfileKind | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchMedicalProfile(patientId);
      setSections(res.sections);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      if (e instanceof ForbiddenError) {
        setHidden(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patientId, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  if (hidden) return null;

  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">Medicinsk profil</h2>
      <p className="mb-sm text-xs text-textSecondary">
        Det patienten angav i hälsodeklarationen, och varje rättelse bredvid.
        Formulärsvaret ändras aldrig.
      </p>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {sections === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      <ul className="flex flex-col divide-y divide-surfaceMuted">
        {(sections ?? []).map((s) => (
          <li key={s.kind} className="py-sm">
            <div className="flex flex-wrap items-baseline gap-sm">
              <h3 className="text-sm font-semibold">{KIND_TITLE[s.kind]}</h3>
              {s.current ? (
                <span className="text-xs text-textSecondary">
                  {s.current.source === "clinician"
                    ? "Rättad av läkare"
                    : "Från hälsodeklarationen"}
                  {s.current.at ? ` · ${fmtDate(s.current.at)}` : ""}
                </span>
              ) : null}
              {editing !== s.kind && !readOnly ? (
                <button
                  type="button"
                  onClick={() => setEditing(s.kind)}
                  className="ml-auto text-xs text-textSecondary underline"
                >
                  {s.current ? "Rätta" : "Komplettera"}
                </button>
              ) : null}
            </div>
            {s.current ? (
              <ul className="mt-xs list-disc pl-md text-sm">
                {describe(s.kind, s.current.value).map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-xs text-sm text-textSecondary">
                Ingen hälsodeklaration inskickad.
              </p>
            )}
            {s.history.some((h) => !h.current) ? (
              <details className="mt-xs text-xs text-textSecondary">
                <summary>Tidigare versioner</summary>
                <ul className="mt-xs flex flex-col gap-xs">
                  {s.history
                    .filter((h) => !h.current)
                    .reverse()
                    .map((h) => (
                      <li key={h.id}>
                        <span className="rounded-pill border border-surfaceMuted px-sm">
                          Ersatt
                        </span>{" "}
                        {h.source === "form"
                          ? "Hälsodeklarationen"
                          : `Rättad av ${h.recordedBy?.name ?? "läkare"}`}
                        {` · ${fmtDate(h.createdAt)}`}
                        <span className="block line-through">
                          {describe(s.kind, h.value).join(" · ")}
                        </span>
                        {h.correctionReason ? (
                          <span className="block">
                            Orsak: {h.correctionReason}
                          </span>
                        ) : null}
                      </li>
                    ))}
                </ul>
              </details>
            ) : null}
            {s.current?.source === "clinician"
              ? (() => {
                  const head = s.history.find((h) => h.current);
                  return head?.correctionReason ? (
                    <p className="mt-xs text-xs text-textSecondary">
                      Orsak ({head.recordedBy?.name ?? "läkare"}):{" "}
                      {head.correctionReason}
                    </p>
                  ) : null;
                })()
              : null}
            {editing === s.kind ? (
              <CorrectionForm
                patientId={patientId}
                section={s}
                onCancel={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  void load();
                }}
                onUnauthorized={onUnauthorized}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

const EMPTY: Record<MedicalProfileKind, MedicalProfileValue> = {
  medications: {
    current: [],
    weightMedicineHistory: null,
    weightMedicineHistoryDetail: null,
  },
  allergies: { hasAllergies: null, detail: null },
  reproductive_status: { status: null },
};

// Same normalisation as the server: trimmed text, empty text as absent, so
// "unchanged" means the same thing on both sides.
function clean(value: MedicalProfileValue): MedicalProfileValue {
  return JSON.parse(
    JSON.stringify(value, (_k, v: unknown) =>
      typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v,
    ),
  ) as MedicalProfileValue;
}

function CorrectionForm({
  patientId,
  section,
  onCancel,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  section: MedicalProfileSectionDto;
  onCancel: () => void;
  onSaved: () => void;
  onUnauthorized?: () => void;
}) {
  const original = section.current?.value ?? EMPTY[section.kind];
  const [value, setValue] = useState<MedicalProfileValue>(() =>
    structuredClone(original),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleaned = clean(value);
  const unchanged = JSON.stringify(cleaned) === JSON.stringify(clean(original));
  const problem =
    section.kind === "medications" &&
    (cleaned as MedicationsValue).current.some((m) => !m.name)
      ? "Varje läkemedel behöver ett namn."
      : null;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await correctMedicalProfile(patientId, {
        kind: section.kind,
        value: cleaned,
        reason: reason.trim(),
        supersedesEntryId: section.current?.entryId ?? null,
      } as CorrectMedicalProfile);
      onSaved();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      const code = e instanceof Error ? e.message : String(e);
      setError(ERROR_TEXT[code] ?? code);
    } finally {
      setBusy(false);
    }
  }

  const input =
    "mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs";

  return (
    <div className="mt-sm rounded-md border border-surfaceMuted p-sm">
      {section.kind === "medications" ? (
        <MedicationsFields
          value={value as MedicationsValue}
          onChange={setValue}
          inputClass={input}
        />
      ) : null}
      {section.kind === "allergies" ? (
        <div className="grid gap-sm sm:grid-cols-2">
          <label className="block text-xs text-textSecondary">
            Kända läkemedelsallergier
            <select
              value={(value as AllergiesValue).hasAllergies ?? ""}
              onChange={(e) =>
                setValue({
                  ...(value as AllergiesValue),
                  hasAllergies:
                    e.target.value === ""
                      ? null
                      : (e.target.value as "ja" | "nej"),
                })
              }
              className={input}
            >
              <option value="">Ej angivet</option>
              <option value="nej">Nej</option>
              <option value="ja">Ja</option>
            </select>
          </label>
          <label className="block text-xs text-textSecondary">
            Substans och reaktion
            <input
              value={(value as AllergiesValue).detail ?? ""}
              onChange={(e) =>
                setValue({
                  ...(value as AllergiesValue),
                  detail: e.target.value,
                })
              }
              className={input}
            />
          </label>
        </div>
      ) : null}
      {section.kind === "reproductive_status" ? (
        <label className="block text-xs text-textSecondary">
          Vad stämmer just nu?
          <select
            value={(value as ReproductiveStatusValue).status ?? ""}
            onChange={(e) =>
              setValue({
                status:
                  e.target.value === ""
                    ? null
                    : (e.target
                        .value as (typeof REPRODUCTIVE_STATUS_VALUES)[number]),
              })
            }
            className={input}
          >
            <option value="">Ej angivet</option>
            {REPRODUCTIVE_STATUS_VALUES.map((v) => (
              <option key={v} value={v}>
                {REPRODUCTIVE_STATUS_LABELS[v]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="mt-sm block text-xs text-textSecondary">
        Orsak (obligatorisk, syns inte för patienten)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className={input}
        />
      </label>
      <p className="mt-xs text-xs text-textSecondary">
        {section.current
          ? "Nuvarande uppgift sparas och visas som ersatt."
          : "Det finns ingen hälsodeklaration att rätta — uppgiften sparas som din komplettering."}
      </p>
      <div className="mt-sm flex gap-xs">
        <button
          type="button"
          disabled={busy || !reason.trim() || unchanged || problem !== null}
          onClick={() => void save()}
          className="rounded-md border border-surfaceMuted px-sm py-xs text-xs disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara rättelse"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded-md px-sm py-xs text-xs text-textSecondary"
        >
          Avbryt
        </button>
      </div>
      {problem ? <p className="mt-xs text-xs text-danger">{problem}</p> : null}
      {unchanged ? (
        <p className="mt-xs text-xs text-textSecondary">
          Samma uppgifter som nu — ingenting att rätta.
        </p>
      ) : null}
      {error ? <p className="mt-xs text-xs text-danger">{error}</p> : null}
    </div>
  );
}

function MedicationsFields({
  value,
  onChange,
  inputClass,
}: {
  value: MedicationsValue;
  onChange: (v: MedicationsValue) => void;
  inputClass: string;
}) {
  const setItem = (i: number, patch: Partial<MedicationItem>) =>
    onChange({
      ...value,
      current: value.current.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    });
  const fields: { key: keyof MedicationItem; label: string }[] = [
    { key: "name", label: "Namn" },
    { key: "strength", label: "Styrka/dos" },
    { key: "frequency", label: "Hur ofta" },
    { key: "reason", label: "Skäl" },
  ];
  return (
    <div className="flex flex-col gap-sm">
      {value.current.map((m, i) => (
        <div
          key={i}
          className="grid grid-cols-2 gap-xs rounded-md border border-surfaceMuted p-xs sm:grid-cols-5"
        >
          {fields.map(({ key, label }) => (
            <label key={key} className="block text-xs text-textSecondary">
              {label}
              <input
                value={m[key] ?? ""}
                onChange={(e) => setItem(i, { [key]: e.target.value })}
                className={inputClass}
              />
            </label>
          ))}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...value,
                current: value.current.filter((_, j) => j !== i),
              })
            }
            className="self-end text-xs text-textSecondary underline"
          >
            Ta bort
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange({
            ...value,
            current: [
              ...value.current,
              { name: "", strength: null, frequency: null, reason: null },
            ],
          })
        }
        className="self-start text-xs text-textSecondary underline"
      >
        Lägg till läkemedel
      </button>
      <div className="grid gap-sm sm:grid-cols-2">
        <label className="block text-xs text-textSecondary">
          Tidigare/nuvarande vikt- eller diabetesläkemedel
          <select
            value={value.weightMedicineHistory ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                weightMedicineHistory:
                  e.target.value === ""
                    ? null
                    : (e.target.value as "nej" | "ja" | "osaker"),
              })
            }
            className={inputClass}
          >
            <option value="">Ej angivet</option>
            <option value="nej">Nej</option>
            <option value="ja">Ja</option>
            <option value="osaker">Osäker</option>
          </select>
        </label>
        <label className="block text-xs text-textSecondary">
          Namn, period, dos, effekt, besvär
          <input
            value={value.weightMedicineHistoryDetail ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                weightMedicineHistoryDetail: e.target.value,
              })
            }
            className={inputClass}
          />
        </label>
      </div>
    </div>
  );
}
